import { useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useEditor } from "@craftjs/core";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { purple, blue } from "@mui/material/colors";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon, Blockchain01Icon, Copy01Icon, Delete01Icon, Delete02Icon, File02Icon, Home07Icon, SidebarRightIcon } from "@hugeicons/core-free-icons";
import useRoutes from "@/hooks/useRoutes";
import { EditorContext } from "@/components/editor/Editor";
import lz from "lzutf8";
import { Drawer, Page, WebsiteElement } from "@/types";
import DrawerSettings from "./DrawerSettings";
import PageSettings from "./PageSettings";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useDeleteElementMutation, useDuplicateElementMutation } from "@/features/templates/templatesApi";
import EmptyState from "@/components/EmptyState";
import useSnackbar from "@/hooks/useSnackbar";

const EMPTY_CONTAINER =
  '{"ROOT":{"type":{"resolvedName":"ContainerBlock"},"isCanvas":true,"props":{},"displayName":"Contenedor","custom":{"displayName":"App"},"hidden":false,"nodes":[],"linkedNodes":{}}}';

const ELEMENT_COLOR = {
  page: blue[500],
  drawer: purple[500],
};

const ELEMENT_ICON = {
  page: File02Icon,
  drawer: SidebarRightIcon,
  default: Blockchain01Icon,
};

const Navigation = () => {
  const [navigate] = useRoutes();
  const { actions } = useEditor();
  const { elements, template, setIsLoading } = useContext(EditorContext);
  const { templateId, elementId } = useParams<{ templateId: string; elementId: string }>();
  const [duplicateElement] = useDuplicateElementMutation();
  const [deleteElement] = useDeleteElementMutation();
  const [openSnackbar] = useSnackbar();

  const handleGoBack = async () => {
    if (!elementId || !template?.data) return;

    try {
      setIsLoading(true);
      navigate("templates.edit", { templateId: String(templateId) });

      // // save
      // const newData = query.serialize();
      // const compressedData = lz.encodeBase64(lz.compress(newData));
      // await onSave(compressedData, elementId);

      // replace editor's data
      const stateToLoad = lz.decompress(lz.decodeBase64(template.data));
      actions.deserialize(stateToLoad);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchElements = async (element: WebsiteElement) => {
    try {
      setIsLoading(true);
      // save data
      // const newData = query.serialize();
      // const compressedData = lz.encodeBase64(lz.compress(newData));
      // await onSave(compressedData, element.id);

      // change element id in url
      navigate("elements.main", { templateId: String(templateId), elementId: String(element?.id) });

      // replace editor state
      if (element?.data) {
        const stateToLoad = lz.decompress(lz.decodeBase64(element?.data));
        actions.deserialize(stateToLoad);
      } else {
        actions.deserialize(EMPTY_CONTAINER);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedElement = useMemo(() => {
    if (!elements) return null;
    return elements.find((element) => element.id === elementId) ?? null;
  }, [elementId, elements]);

  const handleDuplicateElement = async () => {
    if (!selectedElement) {
      openSnackbar("No hay elemento seleccionado para duplicar", "error");
      return;
    }
    const response = await duplicateElement({
      templateId,
      payload: selectedElement,
    });
    if ("error" in response) {
      openSnackbar("Error al duplicar el elemento", "error");
    } else {
      openSnackbar("Elemento duplicado correctamente", "success");
    }
  };

  const handleDeleteElement = async () => {
    if (!selectedElement) {
      openSnackbar("No hay elemento seleccionado para eliminar", "error");
      return;
    }
    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar el elemento "${selectedElement.title}"?
       Esta acción no se puede deshacer.`
    );
    if (!confirmDelete) return;

    const response = await deleteElement({
      templateId,
      elementId: selectedElement.id,
    });
    if ("error" in response) {
      openSnackbar("Error al eliminar el elemento", "error");
    } else {
      openSnackbar("Elemento eliminado correctamente", "success");
    }
  };

  if (!elements?.length) {
    return <EmptyState />;
  }

  return (
    <Stack spacing={2}>
      <Stack>
        <Accordion expanded={false} onChange={handleGoBack}>
          <AccordionSummary>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flex={1}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <HugeiconsIcon icon={Home07Icon} color={ELEMENT_COLOR.page} />
                <Typography component="span">Inicio</Typography>
              </Stack>
            </Stack>
          </AccordionSummary>
        </Accordion>
        {elements.map((element) => (
          <Accordion expanded={elementId === element.id} onChange={() => handleSwitchElements(element)} key={element.id}>
            <AccordionSummary expandIcon={<HugeiconsIcon icon={ArrowDown01Icon} />}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flex={1}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <HugeiconsIcon icon={ELEMENT_ICON[element?.type] ?? ELEMENT_ICON.default} color={ELEMENT_COLOR[element?.type] ?? blue[500]} />
                  <Typography component="span">{element.title}</Typography>
                </Stack>
                {elementId === element.id && (
                  <Stack direction={"row-reverse"} alignItems={"center"}>
                    <IconButton onClick={handleDeleteElement}>
                      <HugeiconsIcon icon={Delete01Icon} size={18} strokeWidth={2} />
                    </IconButton>
                    <IconButton onClick={handleDuplicateElement}>
                      <HugeiconsIcon icon={Copy01Icon} size={18} strokeWidth={2} />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {selectedElement?.type === "drawer" && <DrawerSettings selectedElement={selectedElement as Drawer} />}
              {selectedElement?.type === "page" && <PageSettings selectedElement={selectedElement as Page} />}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" flex={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // Create a new drawer element
          }}
          fullWidth
        >
          Página
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // Create a new drawer element
          }}
          fullWidth
        >
          Drawer
        </Button>
      </Stack>
    </Stack>
  );
};

export default Navigation;
